import { useEffect, useRef, useState } from "react";
import { getUserToken } from "../../utils/Util";
import { ensureFreshToken } from "../../services/tokenRefresh";

const BASE_URL = process.env.REACT_APP_API_URL ?? "https://service.mutfacikservices.com/api/";
const ATTACHMENT_PATH_RE = /\/SupportMail\/DownloadAttachment\/([^/"'?]+)\/([^/"'?]+)/;

const IFRAME_STYLE_SHEET = `
  body { font-family: system-ui, -apple-system, "Segoe UI", sans-serif; font-size: 14px; line-height: 1.55; color: #111827; margin: 0; padding: 10px; word-break: break-word; overflow-wrap: anywhere; }
  img { max-width: 100%; height: auto; }
  a { color: #2563eb; }
  table { max-width: 100%; }
  pre { white-space: pre-wrap; }
`;

interface Props {
  html: string;
}

/**
 * Mail gövdesi backend'de temizlense de (script/stil/iframe atılır), yine de
 * izole bir alanda render edilir: sayfa stilini bozmasın diye sandbox iframe,
 * ek görsellerin token gerektirmesi yüzünden de src'ler blob URL'e çevrilir.
 */
export default function SandboxedMailBody({ html }: Props) {
  const [resolvedHtml, setResolvedHtml] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(80);
  const objectUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    objectUrlsRef.current = [];
    setResolvedHtml(null);

    async function resolve() {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const imgs = Array.from(doc.querySelectorAll("img[src]"));
      const matches = imgs
        .map((img) => ({ img, match: (img.getAttribute("src") ?? "").match(ATTACHMENT_PATH_RE) }))
        .filter((x) => x.match);

      if (matches.length > 0) {
        await ensureFreshToken();
        const token = getUserToken();
        await Promise.all(matches.map(async ({ img, match }) => {
          try {
            const res = await fetch(`${BASE_URL}SupportMail/DownloadAttachment/${match![1]}/${match![2]}`, {
              headers: token ? { Authorization: `Bearer ${token.token}` } : {},
            });
            if (!res.ok) return;
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            objectUrlsRef.current.push(url);
            img.setAttribute("src", url);
          } catch {
            // Görsel yüklenemezse kırık ikon olarak kalır, gövde okunabilirliğini engellemez
          }
        }));
      }

      if (!cancelled) setResolvedHtml(doc.body.innerHTML);
    }

    resolve();
    return () => { cancelled = true; };
  }, [html]);

  useEffect(() => {
    return () => { objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)); };
  }, []);

  const handleLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (doc?.body) setHeight(doc.body.scrollHeight + 12);
  };

  if (resolvedHtml === null) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center" }}>
        <span className="spinner-border spinner-border-sm text-secondary" role="status" />
      </div>
    );
  }

  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>${IFRAME_STYLE_SHEET}</style></head><body>${resolvedHtml}</body></html>`;

  return (
    <iframe
      ref={iframeRef}
      title="mail-govdesi"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      sandbox="allow-same-origin allow-popups"
      style={{ width: "100%", height, border: "none", display: "block", background: "#fff" }}
    />
  );
}
